import angular from 'angular';
import { SUBSCRIPTION_BILLING_CYCLES } from '@sdk/account/constants';

import PauseSubscriptionModalAngular from '@web/account/billing/PauseSubscriptionModal/PauseSubscriptionModalAngular';
import KeepAccountModalAngular from '@web/account/KeepAccountModal/KeepAccountModalAngular';
import { BillingWarningsAngular } from '@web/account/billingWarnings/BillingWarningsAngular';

import BillingPlansAngular from '../../react/account/components/BillingPlansAngular';
import { accessLevels } from '../constants';
import r2a from '../../react/angularIntegration/r2a';
import PurchaseViewAngular from '../../react/account/purchase/PurchaseView/PurchaseViewAngular.tsx';
import BillingViewAngular from '../../react/account/billing/BillingView/BillingViewAngular';
import ModalGuard from '../../react/premiumGuard/ModalGuard/ModalGuardAngular';
import { createCreateOrgPageDirective } from '../../react/account/CreateOrg/CreateOrgPageBridge';

import AccountOverviewCtrl from './AccountOverviewCtrl';
import AccountCtrl from './AccountCtrl';
import InvoicesCtrl from './InvoicesCtrl';
import AchTestingCtrl from './AchTestingCtrl';
import AccountServiceImport from './services/AccountService';
import SubscriptionUtilsImport from './services/SubscriptionUtils';
import SubscriptionImport from './models/Subscription';
import InvoicesImport from './models/Invoices';
import billingSummaryComponent from './components/billingSummaryComponent';
import availableInSubscriptionComponent from './components/availableInSubscriptionComponent';
import toSubscriptionNameFilter from './filters/toSubscriptionName';
import indexUrl from './index.html';
import accountUrl from './account.html';
import invoicesUrl from './invoices.html';
import achTestingUrl from './ach-testing.html';
import { OTHER_BUSINESS_CATEGORY, SUBSCRIPTION_LEVELS } from './constants';
const accountModule = angular.module('account', []);

accountModule.config(($stateProvider) => {
  'ngInject';

  $stateProvider.state('account', {
    abstract: true,
    template: `
      <ui-view></ui-view>
    `,
    url: '/account',
    data: {
      title: 'Account',
      access: accessLevels.manager,
    },
    resolve: {
      personaCollection(PersonaCollection) {
        'ngInject';

        return PersonaCollection.loadData();
      },
      async billingInfo(Authentication, AccountService) {
        'ngInject';

        await Authentication.userLoaded;

        if (
          !Authentication.user ||
          !Authentication.user.orgSettings.subscriptionOptionsEnabled ||
          !Authentication.user.isAllowedToManageSubscription
        ) {
          return null;
        }

        return await AccountService.getBillingInfo();
      },
      async customIndustryName(Authentication) {
        'ngInject';

        if (Authentication.user.org.businessCategory === OTHER_BUSINESS_CATEGORY) {
          const { data } = await Authentication.sendRequest({
            path: 'organisations/customindustry',
          });

          return data.name;
        }

        return '';
      },
    },
  });

  $stateProvider.state('account.overview', {
    templateUrl: indexUrl,
    controllerAs: 'ctrl',
    controller: AccountOverviewCtrl,
    redirectTo: 'account.overview.account',
    url: '',
    data: {
      access: accessLevels.manager,
    },
  });

  $stateProvider.state('account.overview.account', {
    templateUrl: accountUrl,
    controllerAs: 'ctrl',
    controller: AccountCtrl,
    url: '',
    data: {
      access: accessLevels.manager,
    },
  });

  const accountOverviewBillingStateDefinition = {
    url: '/billing?showPricing&fromBillingAddressModal',
    controller: function controller($rootScope) {
      'ngInject';

      this.shouldShowAddressErrors = false;
      $rootScope.$on('billing-address-required-triggered', () => {
        this.shouldShowAddressErrors = true;
      });
    },
    controllerAs: 'ctrl',
    template: `
      <r-billing-view
        billing-info="$resolve.billingInfoWrapped.billingInfo"
        subscription-utils="$resolve.subscriptionUtils"
        currency="$resolve.defaultCurrency"
        is-old-premium="authentication.isOldPremium"
        current-user="authentication.user"
        start-trial="$resolve.accountService.startTrial()"
        go-to-purchase="$resolve.goToPurchase"
        go-to-billing="$resolve.goToBilling()"
        go-to-pricing="$resolve.goToPricing()"
        on-billing-info-updated="$resolve.onBillingInfoUpdated"
        show-pricing="$resolve.showPricing"
        from-billing-address-modal="$resolve.fromBillingAddressModal"
        should-show-address-errors="ctrl.shouldShowAddressErrors"
      ></r-billing-view>
    `,
    data: {
      title: 'Billing',
      access: accessLevels.manager,
    },
    resolve: {
      subscriptionUtils(SubscriptionUtils) {
        'ngInject';

        return SubscriptionUtils;
      },
      defaultCurrency(CurrencyService) {
        'ngInject';

        return CurrencyService.getDefaultCurrency();
      },
      accountService(AccountService) {
        'ngInject';

        return AccountService;
      },
      goToPurchase($state) {
        'ngInject';

        return (plan, isAnnual) => $state.go('account.overview.purchase', { plan, isAnnual });
      },
      goToBilling($state) {
        'ngInject';

        return () => $state.go('account.overview.billing', { showPricing: false });
      },
      goToPricing($state) {
        'ngInject';

        return () => $state.go('account.overview.billing', { showPricing: true });
      },
      billingInfoWrapped(billingInfo) {
        'ngInject';

        return { billingInfo };
      },
      onBillingInfoUpdated(billingInfo, billingInfoWrapped) {
        'ngInject';

        return (data) => {
          Object.assign(billingInfo, data);
          /**
           * The following awkwardness is needed so that the "prop"
           * passed to react has a different reference so it know
           * to rerender the components and stuff
           */
          billingInfoWrapped.billingInfo = { ...billingInfo };
        };
      },
      showPricing($stateParams) {
        'ngInject';

        return $stateParams.showPricing === 'true';
      },
      fromBillingAddressModal($stateParams) {
        'ngInject';

        return $stateParams.fromBillingAddressModal === 'true';
      },
    },
  };

  $stateProvider.state('account.overview.billing', accountOverviewBillingStateDefinition);

  $stateProvider.state('account.overview.invoices', {
    templateUrl: invoicesUrl,
    controllerAs: 'ctrl',
    controller: InvoicesCtrl,
    url: '/invoices',
    data: {
      title: 'Invoices',
      access: accessLevels.manager,
    },
    resolve: {
      invoices(AccountService) {
        'ngInject';

        return AccountService.getInvoices();
      },
    },
  });

  const accountOverviewPurchaseStateDefinition = {
    url: '/purchase?plan&isAnnual',
    template: `
      <r-purchase-view
        initial-level="$resolve.initialLevel"
        initial-billing-cycle="$resolve.initialBillingCycle"
        billing-info="$resolve.billingInfo"
        current-user="authentication.user"
        subscription-utils="$resolve.subscriptionUtils"
        currency="$resolve.defaultCurrency"
        is-old-premium="$root.authentication.isOldPremium"
        post-subscription-purchase="$resolve.accountService.postSubscriptionPurchase"
        are-yearly-subscriptions-enabled="$resolve.areYearlySubscriptionsEnabled"
      ></r-purchase-view>
    `,
    data: {
      title: 'Purchase',
      access: accessLevels.manager,
    },
    resolve: {
      initialLevel(Authentication, $stateParams) {
        'ngInject';

        if ($stateParams.plan && Number($stateParams.plan)) {
          const level = Number($stateParams.plan);

          if (level === 2) {
            // level: 2 means old Premium so it should be Premium if it's an old org
            // or Business for new orgs
            return Authentication.isOldPremium ? 1 : 3;
          }

          return level;
        }

        return SUBSCRIPTION_LEVELS.PREMIUM;
      },
      initialBillingCycle($stateParams) {
        'ngInject';

        if ($stateParams.isAnnual == 'true') {
          return SUBSCRIPTION_BILLING_CYCLES.ANNUAL;
        }

        return SUBSCRIPTION_BILLING_CYCLES.MONTHLY;
      },
      accountService(AccountService) {
        'ngInject';

        return AccountService;
      },
      subscriptionUtils(SubscriptionUtils) {
        'ngInject';

        return SubscriptionUtils;
      },
      defaultCurrency(CurrencyService) {
        'ngInject';

        return CurrencyService.getDefaultCurrency();
      },
      areYearlySubscriptionsEnabled(SubscriptionUtils, billingInfo) {
        'ngInject';

        return SubscriptionUtils.areYearlySubscriptionsEnabled(billingInfo);
      },
    },
  };

  $stateProvider.state('account.overview.purchase', accountOverviewPurchaseStateDefinition);

  $stateProvider.state('account.createOrg', {
    url: '/create-org',
    template: '<r-create-org-page></r-create-org-page>',
    data: {
      title: 'Create Org',
      access: accessLevels.authenticated,
    },
  });

  if (__STAGING__) {
    $stateProvider.state('account.overview.ach-testing', {
      templateUrl: achTestingUrl,
      controller: AchTestingCtrl,
      controllerAs: 'ctrl',
      url: '/ach-testing',
      data: {
        title: 'ACH Testing',
        access: accessLevels.manager,
      },
    });
  }
});

accountModule.run(($transitions, $rootScope) => {
  $transitions.onEnter(
    {
      to: 'account.overview.purchase',
    },
    async (transition) => {
      const billingInfo = await transition.injector().getAsync('billingInfo');

      if (!billingInfo) {
        return transition.router.stateService.target('account.overview.account');
      }

      if (billingInfo.subscriptionStartDate) {
        return transition.router.stateService.target('account.overview.billing');
      }
    }
  );

  $transitions.onEnter(
    {
      to: 'account.overview.(invoices|billing)',
    },
    async (transition) => {
      const billingInfo = await transition.injector().getAsync('billingInfo');

      if (!billingInfo) {
        return transition.router.stateService.target('account.overview.account');
      }
    }
  );

  $transitions.onEnter({ to: 'account.overview.billing' }, async (transition) => {
    const onBillingInfoUpdated = await transition.injector().getAsync('onBillingInfoUpdated');
    const unsubscribe = $rootScope.$on('subscription-updated', onBillingInfoUpdated);
    transition.onExit(unsubscribe);
  });
});

accountModule.service('AccountService', AccountServiceImport);
accountModule.service('SubscriptionUtils', SubscriptionUtilsImport);
accountModule.service('Subscription', SubscriptionImport);
accountModule.service('Invoices', InvoicesImport);

accountModule.component('billingPlans', {
  template: `
    <div>
      <r-billing-plans
        class="margin-top-20"
        callback-after-upgrade="ctrl.callbackAfterUpgrade()"
        subscription-level="$root.authentication.user.org.subscriptionLevel"
        org-settings="$root.authentication.user.orgSettings"
        start-trial="ctrl.AccountService.startTrial()"
        go-to-purchase="ctrl.goToPurchase"
      ></r-billing-plans>
    </div>
  `,
  bindings: {
    stateAfterUpgrade: '<?',
  },
  controllerAs: 'ctrl',
  controller(AccountService, $state) {
    'ngInject';

    this.AccountService = AccountService;

    this.callbackAfterUpgrade = () => {
      if (this.stateAfterUpgrade) {
        const { state, params } = this.stateAfterUpgrade;
        $state.go(state, params);
      }
    };

    this.goToPurchase = (plan, isAnnual) =>
      $state.go('account.overview.purchase', { plan, isAnnual });
  },
});
accountModule.component('rBillingPlans', r2a(BillingPlansAngular));
accountModule.component('billingSummary', billingSummaryComponent);
accountModule.component('availableInSubscription', availableInSubscriptionComponent);
accountModule.component('rPurchaseView', r2a(PurchaseViewAngular));
accountModule.component('rBillingView', r2a(BillingViewAngular));
accountModule.component('rModalGuard', r2a(ModalGuard));
accountModule.component(
  'rBillingWarnings',
  r2a(BillingWarningsAngular, [
    'stateService',
    'currentUser',
    '&saveClientFlags',
    'socketConnection',
  ])
);

accountModule.component('rPauseSubscriptionModalAngular', r2a(PauseSubscriptionModalAngular));
accountModule.component('rKeepAccountModalAngular', r2a(KeepAccountModalAngular));

accountModule.directive('rCreateOrgPage', createCreateOrgPageDirective);

accountModule.filter('toSubscriptionName', toSubscriptionNameFilter);

export default accountModule.name;
